// API client tipado pra `/api/crm/proposals` (Story 11.6 — CRUD + ciclo + numeração).
// Padrão: wrapper sobre `apiClient` (axios) + Zod parse nas respostas (Tier 2).
//
// Fonte de tipos: schemas Zod do `@control-center/shared` (back+front).
// Convenção de envelope do backend:
//  - lista: { items, meta: { next_cursor, total } }
//  - single: { data }
//
// Valores monetários (Decimal) vêm como string do backend (convenção de dinheiro).

import { z } from 'zod';

import {
  crmProposalStatusSchema,
  billingCycleSchema,
  discountTypeSchema,
  proposalPublicViewSchema,
  type ConvertCrmProposalDto,
  type CreateCrmProposalDto,
  type ProposalPublicView,
  type UpdateCrmProposalDto,
} from '@control-center/shared';

import { apiClient, publicApiClient } from './api-client';

// -----------------------------------------------------------------------------
// Schemas de resposta (Tier 2 — valida o shape que o backend devolve)
// -----------------------------------------------------------------------------

const relationSchema = z.object({ id: z.string(), name: z.string() }).nullable();
const dealRelationSchema = z.object({ id: z.string(), title: z.string() }).nullable();

const crmProposalSchema = z.object({
  id: z.string().uuid(),
  proposal_number: z.string(),
  deal_id: z.string().nullable(),
  lead_id: z.string().nullable(),
  client_id: z.string().nullable(),
  saas_product_id: z.string(),
  plan_id: z.string(),
  billing_cycle: billingCycleSchema,
  monthly_value: z.string().nullable(),
  annual_value: z.string().nullable(),
  setup_value: z.string().nullable(),
  discount_amount: z.string().nullable(),
  discount_type: discountTypeSchema.nullable(),
  discount_reason: z.string().nullable(),
  valid_until: z.string(),
  status: crmProposalStatusSchema,
  public_token: z.string().nullable(),
  sent_at: z.string().nullable(),
  viewed_at: z.string().nullable(),
  rejection_reason: z.string().nullable(),
  converted_at: z.string().nullable(),
  converted_subscription_id: z.string().nullable(),
  observations: z.string().nullable(),
  version: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type CrmProposalEntity = z.infer<typeof crmProposalSchema>;

const crmProposalListItemSchema = crmProposalSchema.extend({
  deal: dealRelationSchema,
  lead: relationSchema,
  client: relationSchema,
  saas_product: relationSchema,
  plan: relationSchema,
});
export type CrmProposalListItem = z.infer<typeof crmProposalListItemSchema>;

const crmProposalDetailSchema = crmProposalListItemSchema.extend({
  converted_subscription: z.object({ id: z.string(), status: z.string() }).nullable(),
});
export type CrmProposalDetailEntity = z.infer<typeof crmProposalDetailSchema>;

const listResponseSchema = z.object({
  items: z.array(crmProposalListItemSchema),
  meta: z.object({ next_cursor: z.string().nullable(), total: z.number() }),
});
export type CrmProposalListResponse = z.infer<typeof listResponseSchema>;

const singleResponseSchema = z.object({ data: crmProposalSchema });
const detailResponseSchema = z.object({ data: crmProposalDetailSchema });

export interface CrmProposalListParams {
  cursor?: string;
  limit?: number;
  status?: CrmProposalEntity['status'];
  client_id?: string;
  lead_id?: string;
  deal_id?: string;
  saas_product_id?: string;
  responsible_user_id?: string;
  expiring_days?: number;
  created_from?: string;
  created_to?: string;
}

/** GET /api/crm/proposals — lista paginada por cursor + filtros. */
export async function fetchCrmProposals(
  params: CrmProposalListParams = {},
  signal?: AbortSignal,
): Promise<CrmProposalListResponse> {
  const { data } = await apiClient.get('/api/crm/proposals', { params, signal });
  return listResponseSchema.parse(data);
}

/** GET /api/crm/proposals/:id — detalhe com relações. */
export async function fetchCrmProposal(
  id: string,
  signal?: AbortSignal,
): Promise<CrmProposalDetailEntity> {
  const { data } = await apiClient.get(`/api/crm/proposals/${id}`, { signal });
  return detailResponseSchema.parse(data).data;
}

/** POST /api/crm/proposals — cria proposta (status=rascunho, numeração automática). */
export async function createCrmProposal(payload: CreateCrmProposalDto): Promise<CrmProposalEntity> {
  const { data } = await apiClient.post('/api/crm/proposals', payload);
  return singleResponseSchema.parse(data).data;
}

/** PUT /api/crm/proposals/:id — atualiza (só rascunho, optimistic lock via version). */
export async function updateCrmProposal(
  id: string,
  payload: UpdateCrmProposalDto,
): Promise<CrmProposalEntity> {
  const { data } = await apiClient.put(`/api/crm/proposals/${id}`, payload);
  return singleResponseSchema.parse(data).data;
}

// -----------------------------------------------------------------------------
// Ações de ciclo (Story 11.7) — send/accept/reject/duplicate.
// -----------------------------------------------------------------------------

/** POST /api/crm/proposals/:id/send — rascunho → enviada (gera link público). */
export async function sendCrmProposal(id: string): Promise<CrmProposalEntity> {
  const { data } = await apiClient.post(`/api/crm/proposals/${id}/send`);
  return singleResponseSchema.parse(data).data;
}

/** POST /api/crm/proposals/:id/accept — enviada|visualizada → aceita. */
export async function acceptCrmProposal(id: string): Promise<CrmProposalEntity> {
  const { data } = await apiClient.post(`/api/crm/proposals/${id}/accept`);
  return singleResponseSchema.parse(data).data;
}

/** POST /api/crm/proposals/:id/reject — enviada|visualizada → recusada (motivo obrigatório). */
export async function rejectCrmProposal(
  id: string,
  rejectionReason: string,
): Promise<CrmProposalEntity> {
  const { data } = await apiClient.post(`/api/crm/proposals/${id}/reject`, {
    rejection_reason: rejectionReason,
  });
  return singleResponseSchema.parse(data).data;
}

/** POST /api/crm/proposals/:id/duplicate — clona em novo rascunho (renegociação). */
export async function duplicateCrmProposal(id: string): Promise<CrmProposalEntity> {
  const { data } = await apiClient.post(`/api/crm/proposals/${id}/duplicate`);
  return singleResponseSchema.parse(data).data;
}

// -----------------------------------------------------------------------------
// Conversão (Story 11.9) — proposta aceita → assinatura Owl-managed.
// -----------------------------------------------------------------------------

const convertResponseSchema = z.object({
  data: z.object({
    subscription_id: z.string().uuid(),
    client_id: z.string().uuid(),
    proposal_number: z.string(),
    created: z.boolean(),
  }),
});

export interface ConvertProposalResult {
  subscriptionId: string;
  clientId: string;
  proposalNumber: string;
  created: boolean;
}

/**
 * POST /api/crm/proposals/:id/convert-to-subscription — converte a proposta
 * aceita em assinatura (transação atômica no backend). 201 quando cria, 200
 * quando já estava convertida (idempotente). Erros (400 MISSING_CLIENT_DATA,
 * 409 PROPOSAL_ALREADY_CONVERTED, 500 CONVERSION_FAILED) propagam pro caller.
 */
export async function convertCrmProposal(
  id: string,
  payload: ConvertCrmProposalDto = {},
): Promise<ConvertProposalResult> {
  const { data } = await apiClient.post(
    `/api/crm/proposals/${id}/convert-to-subscription`,
    payload,
  );
  const parsed = convertResponseSchema.parse(data).data;
  return {
    subscriptionId: parsed.subscription_id,
    clientId: parsed.client_id,
    proposalNumber: parsed.proposal_number,
    created: parsed.created,
  };
}

// -----------------------------------------------------------------------------
// Link público read-only (Story 11.7 AC2) — SEM auth (publicApiClient).
// -----------------------------------------------------------------------------

const publicViewResponseSchema = z.object({ data: proposalPublicViewSchema });

/**
 * GET /public/proposals/:token — proposta read-only (cliente vê sem login).
 * Usa publicApiClient (sem header de auth, sem redirect de 401). Erros HTTP
 * (404 token inválido / 410 expirada / 429 rate limit) propagam pro caller.
 */
export async function fetchPublicProposal(
  token: string,
  signal?: AbortSignal,
): Promise<ProposalPublicView> {
  const { data } = await publicApiClient.get(`/public/proposals/${token}`, { signal });
  return publicViewResponseSchema.parse(data).data;
}
